import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, FileText, Book, File, AlertCircle, CheckCircle, Loader, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useProcessing } from '../../contexts/ProcessingContext';
import api from '../../services/api';
import ContentSafetySettings from '../features/ContentSafetySettings';
import { useTranslation } from "react-i18next";
import {
  MAX_PDF_FILE_SIZE_MB,
  MAX_PDF_PAGE_COUNT,
  validatePdfForUpload
} from '../../utils/pdfUploadValidation';

const DocumentUploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const { t } = useTranslation();
  const { user, getToken } = useAuth();
  const { startProcessing } = useProcessing();
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [instanceTitle, setInstanceTitle] = useState('');
  const [instanceDescription, setInstanceDescription] = useState('');
  const [safetySettings, setSafetySettings] = useState({});
  const [contentType, setContentType] = useState('auto'); // auto, narrative, explanatory
  const [preserveOriginal, setPreserveOriginal] = useState(false);
  const [wordsPerPost, setWordsPerPost] = useState(200);
  const [uploading, setUploading] = useState(false);
  const [validatingFile, setValidatingFile] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [processingError, setProcessingError] = useState('');
  const [documentId, setDocumentId] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [processingDocuments, setProcessingDocuments] = useState([]);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [processingTime, setProcessingTime] = useState(0);
  const [canCancel, setCanCancel] = useState(false);
  const fileInputRef = useRef(null);

  // Feature flags: Only Auto-Detect mode is enabled in production
  // In development, all modes are available for testing
  const isDevelopment = process.env.NODE_ENV === 'development';

  const contentTypeSelectionDisabled = preserveOriginal || uploading || processing || success;
  const preserveControlsDisabled = uploading || processing || success;

  // Check for processing documents when modal opens
  useEffect(() => {
    if (isOpen && user) {
      checkForProcessingDocuments();
    }
  }, [isOpen, user?.id]);

  const checkForProcessingDocuments = async () => {
    if (!user?.id) return;

    try {
      setCheckingStatus(true);
      const response = await api.get('/documents/processing-status', {
        params: { user_id: user.id }
      });
      const { processing_documents = [] } = response.data;
      setProcessingDocuments(processing_documents);
    } catch (error) {
      console.error('Error checking processing status:', error);
      // Graceful degradation - allow upload if check fails
      setProcessingDocuments([]);
    } finally {
      setCheckingStatus(false);
    }
  };

  // GAP-UI-002: Track processing time and enable cancel after 5 minutes
  useEffect(() => {
    if (processingDocuments.length > 0) {
      const doc = processingDocuments[0];
      const startTime = new Date(doc.processing_started_at || doc.created_at);

      const interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000); // seconds
        setProcessingTime(elapsed);

        // Enable cancel button after 5 minutes (300 seconds)
        setCanCancel(elapsed > 300);
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setProcessingTime(0);
      setCanCancel(false);
    }
  }, [processingDocuments]);

  const handleCancelProcessing = async () => {
    if (processingDocuments.length === 0) return;

    const docToCancel = processingDocuments[0];

    // Confirm with user
    if (!window.confirm(`Are you sure you want to cancel processing for "${docToCancel.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      console.log('🛑 GAP-UI-002: Cancelling document:', docToCancel.id);

      const response = await api.post(`/documents/cancel/${docToCancel.id}`);

      if (response.data.success) {
        console.log('✅ GAP-UI-002: Document cancelled successfully');

        // Refresh processing status
        await checkForProcessingDocuments();

        // Show success message
        alert('Processing cancelled successfully. You can now upload a new document.');
      }
    } catch (error) {
      console.error('❌ GAP-UI-002: Failed to cancel processing:', error);
      alert('Failed to cancel processing. Please try again or contact support.');
    }
  };

  if (!isOpen) return null;

  // Helper functions for file type detection and validation
  // TEMPORARY RESTRICTION (GAP-UPLOAD-001): Only PDF is supported
  const getSupportedFormats = () => ({
    'application/pdf': { extension: '.pdf', icon: FileText, name: 'PDF' }
  });

  const isSupportedFileType = (file) => {
    // Check mime type first
    if (file.type === 'application/pdf') {
      return true;
    }

    // Fallback to extension check
    const extension = file.name.toLowerCase().match(/\.[^.]*$/)?.[0];
    return extension === '.pdf';
  };

  // TEMPORARY RESTRICTION (GAP-UPLOAD-001): Only PDF is supported
  const getFileIcon = () => {
    return FileText; // PDF icon
  };

  const getFileTypeName = () => {
    return 'PDF';
  };

  const resetForm = () => {
    setSelectedFile(null);
    setInstanceTitle('');
    setInstanceDescription('');
    setSafetySettings({});
    setContentType('auto');
    setPreserveOriginal(false);
    setWordsPerPost(200);
    setError('');
    setSuccess(false);
    setUploading(false);
    setProcessing(false);
    setProcessingStatus('');
    setProcessingError('');
    setDocumentId(null);
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    // Always allow closing after processing starts (processing happens in background)
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    resetForm();
    onClose();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const supportedFile = files.find(file => isSupportedFileType(file));

    if (supportedFile) {
      await handleFileSelect(supportedFile);
    } else {
      setError('Please select a PDF file.');
    }
  };

  const handleFileInputChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await handleFileSelect(file);
    }
  };

  const handleFileSelect = async (file) => {
    setSelectedFile(null);
    setError('');
    setValidatingFile(true);

    try {
      await validatePdfForUpload(file);
      setSelectedFile(file);

      // Auto-generate instance title from filename
      if (!instanceTitle) {
        const title = file.name.replace(/\.pdf$/i, '').replace(/[-_]/g, ' ');
        setInstanceTitle(title);
      }
    } catch (validationError) {
      setError(validationError.message || 'We could not validate this PDF.');
    } finally {
      setValidatingFile(false);
    }
  };

  const validateForm = () => {
    if (!selectedFile) {
      setError('Please select a document file');
      return false;
    }
    if (!instanceTitle.trim()) {
      setError('Instance title is required');
      return false;
    }
    return true;
  };

  const createInstance = async () => {
    try {
      const response = await api.post('/documents/instances/create', {
        title: instanceTitle.trim(),
        description: instanceDescription.trim() || `Learning instance for ${instanceTitle.trim()}`,
        user_id: user?.id,
        safety_settings: safetySettings,
        content_processing_type: contentType
      });

      const instanceId = response.data.instance.id;

      return instanceId;
    } catch (error) {
      console.error('Error creating instance:', error);
      throw new Error(error.response?.data?.error || 'Failed to create instance');
    }
  };

  const uploadPDF = async (instanceId, file = selectedFile) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('instance_id', instanceId);
    formData.append('filename', instanceTitle.trim());
    formData.append('user_id', user?.id);
    formData.append('contentType', contentType);
    formData.append('preserveOriginal', preserveOriginal.toString());
    formData.append('wordsPerPost', wordsPerPost.toString());

    try {
      const response = await api.post('/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${getToken()}`
        }
      });

      return response.data.document.id;
    } catch (error) {
      console.error('Error uploading PDF:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw new Error(error.response?.data?.error || 'Failed to upload PDF');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    // PART 1 FIX: Prevent duplicate uploads (double-click protection)
    if (uploading) {
      console.warn('⚠️ Upload already in progress - ignoring duplicate submission');
      return; // Debounce: prevent double-click
    }

    try {
      setError('');
      setUploading(true);

      // Step 1: Create instance
      console.log('📝 Creating new instance...');
      const instanceId = await createInstance();

      // Store file reference before clearing state
      const fileToUpload = selectedFile;

      // Step 2: Close modal immediately and show background processing message
      setUploading(false);
      setSuccess(true);

      // Call success callback immediately with instance info
      onUploadSuccess({
        documentId: null, // Will be set later when upload completes
        instanceId,
        title: instanceTitle,
        filename: selectedFile.name,
        instance: {
          id: instanceId,
          name: instanceTitle,
          status: 'processing',
          posts_count: 0,
          thumbnail: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=200&fit=crop'
        }
      });

      // Close modal with enhanced success message
      setTimeout(() => {
        handleClose();

        // Show enhanced success notification
        const notification = document.createElement('div');
        notification.innerHTML = `
          <div style="position: fixed; top: 20px; right: 20px; z-index: 10000; 
                      background: linear-gradient(135deg, #10B981 0%, #059669 100%); 
                      color: white; border-radius: 16px; 
                      padding: 24px; box-shadow: 0 20px 40px rgba(16, 185, 129, 0.3); 
                      max-width: 420px; animation: slideInRight 0.4s ease-out;">
            <div style="display: flex; align-items: center; margin-bottom: 12px;">
              <div style="font-size: 24px; margin-right: 12px;">🎉</div>
              <div style="font-weight: 700; font-size: 16px;">
                ${t('upload.notifications.success_title')}
              </div>
            </div>
            <div style="color: rgba(255, 255, 255, 0.9); font-size: 14px; margin-bottom: 18px; line-height: 1.4;">
              ${t('upload.notifications.success_desc', { title: instanceTitle })}
            </div>
            <div style="display: flex; gap: 10px; flex-wrap: wrap;">
              <button onclick="localStorage.setItem('notify_${instanceId}', 'true'); 
                             this.parentElement.parentElement.style.display='none';
                             const successAlert = document.createElement('div');
                             successAlert.style.cssText = 'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: #10B981; color: white; padding: 20px 40px; border-radius: 12px; z-index: 10001; font-weight: 600; box-shadow: 0 10px 30px rgba(0,0,0,0.3);';
                             successAlert.textContent = '🔔 ${t('upload.notifications.notify_me')}';
                             document.body.appendChild(successAlert);
                             setTimeout(() => document.body.removeChild(successAlert), 3000);"
                      style="background: rgba(255, 255, 255, 0.2); backdrop-filter: blur(10px); 
                             color: white; padding: 8px 16px; border-radius: 8px; 
                             font-size: 13px; border: 1px solid rgba(255,255,255,0.3); 
                             cursor: pointer; transition: all 0.2s; font-weight: 500;"
                      onmouseover="this.style.background='rgba(255,255,255,0.3)'"
                      onmouseout="this.style.background='rgba(255,255,255,0.2)'">
                🔔 ${t('upload.notifications.notify_me')}
              </button>
              <button onclick="const profileTab = document.querySelector('[role=tab][aria-controls*=Profile]') || document.querySelector('[aria-label*=Profile]') || Array.from(document.querySelectorAll('[role=tab]')).find(t => t.textContent.includes('Profile'));
                             if (profileTab) { profileTab.click(); }
                             this.parentElement.parentElement.style.display='none';"
                      style="background: rgba(255, 255, 255, 0.95); color: #059669; 
                             padding: 8px 16px; border-radius: 8px; font-size: 13px; 
                             border: none; cursor: pointer; transition: all 0.2s; font-weight: 600;"
                      onmouseover="this.style.background='white'; this.style.transform='translateY(-1px)'"
                      onmouseout="this.style.background='rgba(255,255,255,0.95)'; this.style.transform='translateY(0)'">
                📊 ${t('upload.notifications.dashboard')}
              </button>
            </div>
            <button onclick="this.parentElement.style.display='none';"
                    style="position: absolute; top: 8px; right: 12px; background: transparent; 
                           border: none; color: rgba(255,255,255,0.7); font-size: 20px; 
                           cursor: pointer; padding: 4px; border-radius: 4px;"
                    onmouseover="this.style.color='white'; this.style.background='rgba(255,255,255,0.1)'"
                    onmouseout="this.style.color='rgba(255,255,255,0.7)'; this.style.background='transparent'">
              ×
            </button>
          </div>
          <style>
            @keyframes slideInRight {
              from { transform: translateX(100%); opacity: 0; }
              to { transform: translateX(0); opacity: 1; }
            }
          </style>
        `;
        document.body.appendChild(notification);

        // Auto-remove notification after 8 seconds
        setTimeout(() => {
          if (notification.parentNode) {
            notification.parentNode.removeChild(notification);
          }
        }, 8000);
      }, 500);

      // Step 3: Start upload and processing in background (don't await)
      handleBackgroundUploadAndProcess(instanceId, fileToUpload);

      console.log('✅ Instance created and background processing started.');

    } catch (error) {
      console.error('❌ Instance creation failed:', error);

      // Provide user-friendly error messages
      let userMessage = error.message || 'Upload failed';

      if (error.response?.status === 429) {
        userMessage = 'Daily upload limit reached (3/3). Please try again tomorrow.';
      } else if (error.response?.status === 400 && error.response?.data?.error?.includes('page limit')) {
        userMessage = error.response.data.error;
      } else if (error.message?.includes('Network Error')) {
        userMessage = 'Connection failed. Please check your internet connection and try again.';
      } else if (error.message?.includes('413') || error.message?.includes('too large')) {
        userMessage = `This PDF is too large. The maximum file size is ${MAX_PDF_FILE_SIZE_MB}MB.`;
      } else if (error.message?.includes('401') || error.message?.includes('unauthorized')) {
        userMessage = 'Authentication failed. Please refresh and try again.';
      } else if (error.message?.includes('500')) {
        userMessage = 'Server error. Please try again in a few minutes.';
      } else if (error.message?.includes('timeout')) {
        userMessage = 'Upload timed out. Please try again with a smaller file.';
      }

      setError(userMessage);
      setUploading(false);
      setProcessing(false);
    }
  };

  // Handle background upload and processing
  const handleBackgroundUploadAndProcess = async (instanceId, file) => {
    try {
      // Start upload in background
      console.log('Starting background upload for instance:', instanceId);
      const documentId = await uploadPDF(instanceId, file);

      console.log('Upload completed for instance:', instanceId);
      setDocumentId(documentId);

      // Start processing polling
      setProcessing(true);
      setProcessingStatus('processing');

      // Poll for completion
      const interval = setInterval(async () => {
        await checkProcessingStatus(documentId);
      }, 5000);

      setPollingInterval(interval);

    } catch (error) {
      console.error('Background upload/process error:', error);

      const errorNotification = document.createElement('div');
      const notificationCard = document.createElement('div');
      const notificationTitle = document.createElement('div');
      const notificationMessage = document.createElement('div');
      const closeButton = document.createElement('button');

      notificationCard.style.cssText = 'position: fixed; top: 20px; right: 20px; z-index: 10000; background: white; border: 2px solid #EF4444; border-radius: 12px; padding: 20px; box-shadow: 0 10px 25px rgba(0,0,0,0.15); max-width: 400px;';
      notificationTitle.style.cssText = 'font-weight: 600; color: #DC2626; margin-bottom: 8px;';
      notificationMessage.style.cssText = 'color: #6B7280; font-size: 14px; margin-bottom: 12px;';
      closeButton.style.cssText = 'background: #EF4444; color: white; padding: 6px 12px; border-radius: 6px; font-size: 12px; border: none; cursor: pointer;';

      notificationTitle.textContent = `Upload failed for ${instanceTitle}`;
      notificationMessage.textContent = error?.message || 'Please try uploading the PDF again.';
      closeButton.textContent = 'Close';
      closeButton.addEventListener('click', () => errorNotification.remove());

      notificationCard.append(notificationTitle, notificationMessage, closeButton);
      errorNotification.appendChild(notificationCard);
      document.body.appendChild(errorNotification);

      // Auto-remove error notification after 10 seconds
      setTimeout(() => {
        if (errorNotification.parentNode) {
          errorNotification.parentNode.removeChild(errorNotification);
        }
      }, 10000);
    }
  };

  const checkProcessingStatus = async (docId) => {
    try {
      const response = await api.get(`/documents/processing-status`);
      const { failed_documents = [] } = response.data;

      // Check if our document failed
      const failedDoc = failed_documents.find(doc => doc.id === docId);

      if (failedDoc) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
        setProcessingStatus('failed');

        // Extract meaningful error message
        let errorMessage = failedDoc.error || 'Processing failed';
        if (errorMessage.includes('OCR is required')) {
          errorMessage = 'Your PDF appears to be scanned/image-based and requires OCR processing, which is currently unavailable. Please try a text-based PDF or contact support.';
        } else if (errorMessage.includes('insufficient content')) {
          errorMessage = 'Unable to extract sufficient content from this PDF. The file may be corrupted or use unsupported features.';
        }

        setProcessingError(errorMessage);
        return;
      }

      // Check if processing completed successfully by looking for generated content
      try {
        // First try to get document info to find the instance ID
        const docResponse = await api.get(`/documents/${docId}`);
        if (docResponse.data.document && docResponse.data.document.instance_id) {
          const instanceId = docResponse.data.document.instance_id;

          // Check if instance has generated posts/content
          const postsResponse = await api.get(`/feed/${instanceId}`);
          if (postsResponse.data.posts && postsResponse.data.posts.length > 0) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
            setProcessingStatus('completed');
            setSuccess(true);

            // Auto-close after showing success
            setTimeout(() => {
              handleClose();
            }, 3000);
          }
        }
      } catch (instanceError) {
        // Instance endpoint might not exist yet or no posts generated, continue polling
        console.log('Instance not ready yet or no posts generated, continuing to poll...');
      }

    } catch (error) {
      console.error('Error checking processing status:', error);
    }
  };

  const reportBug = async () => {
    if (!documentId) return;

    try {
      await api.post('/documents/report-bug', {
        document_id: documentId,
        user_report: `Processing failed for document: ${selectedFile?.name}. Error: ${processingError}`,
        browser_info: navigator.userAgent,
        file_size: selectedFile?.size,
        timestamp: new Date().toISOString()
      });

      setProcessingError(prevError => prevError + ' (Bug report sent to development team)');
    } catch (error) {
      console.error('Error reporting bug:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    }
    return `${Math.round(bytes / (1024 * 1024))} MB`;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

      <div className="relative z-50 min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">{t('upload.title')}</h2>
            <button
              onClick={handleClose}
              disabled={uploading}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors"
              title={t('common.cancel')}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Checking Status Loader */}
            {checkingStatus && (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <Loader className="w-12 h-12 text-blue-600 animate-spin" />
                <p className="text-gray-600 text-sm">{t('upload.checking_status')}</p>
              </div>
            )}

            {/* Blocking UI - Documents Currently Processing */}
            {!checkingStatus && processingDocuments.length > 0 && (
              <div className="text-center py-8">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
                    <Loader className="w-8 h-8 text-blue-600 animate-spin" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('upload.currently_processing')}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    {t('upload.is_being_processed', { title: processingDocuments[0].title })}
                  </p>
                  <p className="text-xs text-gray-500 mb-4">
                    {t('upload.processing_time', { minutes: Math.floor(processingTime / 60), seconds: processingTime % 60 })}
                  </p>
                  <p className="text-sm text-gray-600">
                    {t('upload.wait_finish')}
                  </p>
                </div>

                {/* GAP-UI-002: Show cancel button after 5 minutes */}
                {canCancel && (
                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800 mb-3">
                      {t('upload.cancel_desc')}
                    </p>
                    <button
                      onClick={handleCancelProcessing}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
                      data-testid="cancel-processing-button"
                    >
                      {t('upload.cancel_processing')}
                    </button>
                  </div>
                )}

                <div className="mt-4 space-y-2">
                  <button
                    onClick={() => {
                      onClose();
                      // Optionally navigate to dashboard
                    }}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {t('upload.go_to_dashboard')}
                  </button>
                  <button
                    onClick={() => checkForProcessingDocuments()}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    {t('upload.refresh_status')}
                  </button>
                </div>
              </div>
            )}

            {/* Normal Upload Form - Only shown when NO processing documents */}
            {!checkingStatus && processingDocuments.length === 0 && (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Display */}
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}

                {/* Success Display */}
                {(success || processingStatus === 'completed') && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-green-800 text-sm font-medium">Document processed successfully!</p>
                      <p className="text-green-700 text-xs mt-1">Your learning content is now available.</p>
                    </div>
                  </div>
                )}

                {/* Processing Status Display */}
                {processingStatus === 'processing' && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start space-x-3">
                    <Loader className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5 animate-spin" />
                    <div>
                      <p className="text-blue-800 text-sm font-medium">Processing your document...</p>
                      <p className="text-blue-700 text-xs mt-1">This may take 1-3 minutes. Please wait.</p>
                    </div>
                  </div>
                )}

                {/* Processing Error Display */}
                {processingStatus === 'failed' && processingError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-red-800 text-sm font-medium">Processing Failed</p>
                        <p className="text-red-700 text-xs mt-1">{processingError}</p>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={reportBug}
                        className="text-xs bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded transition-colors"
                      >
                        Report Issue
                      </button>
                      <button
                        onClick={handleClose}
                        className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1 rounded transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}

                {/* Processing Timeout Display */}
                {processingStatus === 'timeout' && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-yellow-800 text-sm font-medium">Processing Taking Longer Than Expected</p>
                      <p className="text-yellow-700 text-xs mt-1">Your document may require special processing. Check back later or contact support.</p>
                    </div>
                  </div>
                )}

                {/* File Drop Zone */}
                {!selectedFile && (
                  <div
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${dragOver
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-300 hover:border-purple-400 hover:bg-purple-25'
                      }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">
                      {t('upload.drop_zone.title')}{' '}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-purple-600 hover:text-purple-700 font-medium"
                        disabled={uploading || processing || validatingFile}
                      >
                        {t('upload.drop_zone.browse')}
                      </button>
                    </p>
                    <p className="text-gray-500 text-sm">
                      {validatingFile
                        ? `Checking PDF against the ${MAX_PDF_FILE_SIZE_MB}MB and ${MAX_PDF_PAGE_COUNT}-page limits…`
                        : t('upload.drop_zone.formats')}
                    </p>
                    <p className="text-gray-500 text-xs mt-1">{t('upload.drop_zone.max_size')}</p>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf"
                      onChange={handleFileInputChange}
                      className="hidden"
                      disabled={uploading || processing || validatingFile}
                    />
                  </div>
                )}

                {/* Selected File Display */}
                {selectedFile && (
                  <div className="border border-gray-200 rounded-xl p-4 flex items-start space-x-4">
                    {React.createElement(getFileIcon(), {
                      className: 'w-8 h-8 text-red-500 flex-shrink-0'
                    })}
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 font-medium truncate">{selectedFile.name}</p>
                      <p className="text-gray-500 text-sm">{getFileTypeName()} • {formatFileSize(selectedFile.size)}</p>
                    </div>
                    {!uploading && !processing && !success && (
                      <button
                        type="button"
                        onClick={() => setSelectedFile(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                )}

                {/* Instance Details */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('upload.form.instance_title')} *
                    </label>
                    <input
                      type="text"
                      value={instanceTitle}
                      onChange={(e) => setInstanceTitle(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="e.g., Romeo and Juliet Study Guide"
                      disabled={uploading || processing || success}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('upload.form.description')}
                    </label>
                    <textarea
                      value={instanceDescription}
                      onChange={(e) => setInstanceDescription(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
                      placeholder="Brief description of what you'll learn from this document..."
                      rows={3}
                      disabled={uploading || processing || success}
                    />
                  </div>

                  {/* Content Processing Type Selection - 2x2 Grid */}
                  {selectedFile && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        {t('upload.form.processing_type')} *
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {/* Auto-Detect Option */}
                        <div
                          className={`border rounded-xl p-4 transition-all ${contentType === 'auto'
                              ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                              : 'border-gray-300 hover:border-purple-300'
                            } ${contentTypeSelectionDisabled ? 'opacity-60 cursor-not-allowed pointer-events-none' : 'cursor-pointer'}`}
                          onClick={() => {
                            if (contentTypeSelectionDisabled) return;
                            setContentType('auto');
                            setPreserveOriginal(false);
                          }}
                          aria-disabled={contentTypeSelectionDisabled}
                        >
                          <div className="flex flex-col space-y-2">
                            <div className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name="contentType"
                                value="auto"
                                checked={contentType === 'auto' && !preserveOriginal}
                                onChange={(e) => {
                                  setContentType(e.target.value);
                                  setPreserveOriginal(false);
                                }}
                                className="text-purple-600 focus:ring-purple-500"
                                disabled={contentTypeSelectionDisabled}
                              />
                              <div className="font-medium text-gray-900 text-sm">🤖 {t('upload.form.auto_detect.title')}</div>
                            </div>
                            <div className="text-xs text-gray-600">
                              {t('upload.form.auto_detect.desc')}
                            </div>
                          </div>
                        </div>

                        {/* Narrative Content Option */}
                        <div
                          className={`border rounded-xl p-4 transition-all relative group ${contentType === 'narrative'
                              ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                              : !isDevelopment
                                ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                                : 'border-gray-300 hover:border-purple-300'
                            } ${contentTypeSelectionDisabled || !isDevelopment ? 'opacity-60 cursor-not-allowed pointer-events-none' : 'cursor-pointer'}`}
                          onClick={() => {
                            if (contentTypeSelectionDisabled || !isDevelopment) return;
                            setContentType('narrative');
                            setPreserveOriginal(false);
                          }}
                          aria-disabled={contentTypeSelectionDisabled || !isDevelopment}
                          title={!isDevelopment ? "This mode is coming soon. Use Auto-Detect for now." : ""}
                        >
                          <div className="flex flex-col space-y-2">
                            <div className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name="contentType"
                                value="narrative"
                                checked={contentType === 'narrative' && !preserveOriginal}
                                onChange={(e) => {
                                  setContentType(e.target.value);
                                  setPreserveOriginal(false);
                                }}
                                className="text-purple-600 focus:ring-purple-500"
                                disabled={contentTypeSelectionDisabled || !isDevelopment}
                              />
                              <div className={`font-medium text-sm ${!isDevelopment ? 'text-gray-400' : 'text-gray-900'}`}>📚 {t('upload.form.narrative.title')}</div>
                            </div>

                            {/* Coming Soon Badge - Below Title */}
                            {!isDevelopment && (
                              <div className="flex items-center justify-start">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                                  🔒 {t('upload.form.coming_soon')}
                                </span>
                              </div>
                            )}

                            <div className={`text-xs ${!isDevelopment ? 'text-gray-400' : 'text-gray-600'}`}>
                              {t('upload.form.narrative.desc')}
                            </div>
                          </div>

                          {/* Hover Tooltip - Only in production */}
                          {!isDevelopment && (
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                              <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
                                This mode is coming soon. Use Auto-Detect for now.
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                  <div className="border-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Explanatory Content Option */}
                        <div
                          className={`border rounded-xl p-4 transition-all relative group ${contentType === 'explanatory'
                              ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                              : !isDevelopment
                                ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                                : 'border-gray-300 hover:border-purple-300'
                            } ${contentTypeSelectionDisabled || !isDevelopment ? 'opacity-60 cursor-not-allowed pointer-events-none' : 'cursor-pointer'}`}
                          onClick={() => {
                            if (contentTypeSelectionDisabled || !isDevelopment) return;
                            setContentType('explanatory');
                            setPreserveOriginal(false);
                          }}
                          aria-disabled={contentTypeSelectionDisabled || !isDevelopment}
                          title={!isDevelopment ? "This mode is coming soon. Use Auto-Detect for now." : ""}
                        >
                          <div className="flex flex-col space-y-2">
                            <div className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name="contentType"
                                value="explanatory"
                                checked={contentType === 'explanatory' && !preserveOriginal}
                                onChange={(e) => {
                                  setContentType(e.target.value);
                                  setPreserveOriginal(false);
                                }}
                                className="text-purple-600 focus:ring-purple-500"
                                disabled={contentTypeSelectionDisabled || !isDevelopment}
                              />
                              <div className={`font-medium text-sm ${!isDevelopment ? 'text-gray-400' : 'text-gray-900'}`}>🔬 {t('upload.form.explanatory.title')}</div>
                            </div>

                            {/* Coming Soon Badge - Below Title */}
                            {!isDevelopment && (
                              <div className="flex items-center justify-start">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                                  🔒 {t('upload.form.coming_soon')}
                                </span>
                              </div>
                            )}

                            <div className={`text-xs ${!isDevelopment ? 'text-gray-400' : 'text-gray-600'}`}>
                              {t('upload.form.explanatory.desc')}
                            </div>
                          </div>

                          {/* Hover Tooltip - Only in production */}
                          {!isDevelopment && (
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                              <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
                                This mode is coming soon. Use Auto-Detect for now.
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                  <div className="border-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Preserve Original Option */}
                        <div
                          className={`border rounded-xl p-4 transition-all relative group ${preserveOriginal
                              ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                              : !isDevelopment
                                ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                                : 'border-gray-300 hover:border-purple-300'
                            } ${preserveControlsDisabled || !isDevelopment ? 'opacity-60 cursor-not-allowed pointer-events-none' : 'cursor-pointer'}`}
                          onClick={() => {
                            if (preserveControlsDisabled || !isDevelopment) return;
                            setContentType('preserve');
                            setPreserveOriginal(true);
                          }}
                          aria-disabled={preserveControlsDisabled || !isDevelopment}
                          title={!isDevelopment ? "This mode is coming soon. Use Auto-Detect for now." : ""}
                        >
                          <div className="flex flex-col space-y-2">
                            <div className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name="contentType"
                                value="preserve"
                                checked={preserveOriginal}
                                onChange={() => {
                                  setContentType('preserve');
                                  setPreserveOriginal(true);
                                }}
                                className="text-purple-600 focus:ring-purple-500"
                                disabled={preserveControlsDisabled || !isDevelopment}
                              />
                              <div className={`font-medium text-sm ${!isDevelopment ? 'text-gray-400' : 'text-gray-900'}`}>📄 {t('upload.form.preserve.title')}</div>
                            </div>

                            {/* Coming Soon Badge - Below Title */}
                            {!isDevelopment && (
                              <div className="flex items-center justify-start">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                                  🔒 {t('upload.form.coming_soon')}
                                </span>
                              </div>
                            )}

                            <div className={`text-xs ${!isDevelopment ? 'text-gray-400' : 'text-gray-600'}`}>
                              {t('upload.form.preserve.desc')}
                            </div>
                          </div>

                          {/* Hover Tooltip - Only in production */}
                          {!isDevelopment && (
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                              <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
                                This mode is coming soon. Use Auto-Detect for now.
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                  <div className="border-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Words Per Post Control - Shows when Preserve Original is selected */}
                      {preserveOriginal && (
                        <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            <span className="text-purple-600">{t('upload.form.words_per_post', { count: wordsPerPost })}</span>
                          </label>
                          <input
                            type="range"
                            min="50"
                            max="180"
                            step="10"
                            value={wordsPerPost}
                            onChange={(e) => setWordsPerPost(parseInt(e.target.value, 10))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                            disabled={preserveControlsDisabled}
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-2">
                            <span>50 words</span>
                            <span>180 words</span>
                          </div>
                          <div className="mt-3 text-xs text-gray-600">
                            Tip: Posts support a maximum of 180 words for optimal display.
                          </div>
                        </div>
                      )}

                      {/* Processing Impact Info */}
                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="text-sm text-blue-800">
                          <strong>💡 {t('upload.info.processing_impact')}:</strong>
                          <ul className="mt-2 space-y-1 text-xs">
                            <li>• <strong>{t('upload.form.auto_detect.title')}:</strong> Analyzes content type automatically for optimal results</li>
                            <li>• <strong>{t('upload.form.narrative.title')}:</strong> Maintains original wording for 90%+ source accuracy</li>
                            <li>• <strong>{t('upload.form.explanatory.title')}:</strong> Simplifies complex concepts while preserving meaning</li>
                            <li>• <strong>{t('upload.form.preserve.title')}:</strong> No AI summarization - exact text from source</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Content Safety Settings */}
                  {/* {selectedFile && (
                <div className="border-t border-gray-200 pt-6">
                  <ContentSafetySettings
                    settings={safetySettings}
                    onUpdate={setSafetySettings}
                  />
                </div>
              )} */}
                </div>
                <p className="mb-4 text-center text-xs leading-relaxed text-slate-600">
                  By uploading, you agree to our{' '}
                  <a href="/terms" className="font-semibold text-purple-700 underline underline-offset-2 hover:text-purple-800">
                    Terms of Service
                  </a>{' '}
                  and acknowledge our{' '}
                  <a href="/privacy" className="font-semibold text-purple-700 underline underline-offset-2 hover:text-purple-800">
                    Privacy Policy
                  </a>, including AI processing of your document by OpenAI.
                </p>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={uploading || processing || validatingFile || (success && processingStatus !== 'failed') || !selectedFile || !instanceTitle.trim()}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 rounded-xl font-medium hover:from-purple-700 hover:to-blue-700 focus:ring-4 focus:ring-purple-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2"
                  onClick={(e) => {
                    // Ensure form submission doesn't close modal accidentally
                    e.stopPropagation();
                  }}
                >
                  {uploading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>{t('upload.submit_button.creating')}</span>
                    </>
                  ) : processing ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>{t('upload.submit_button.starting')}</span>
                    </>
                  ) : (success && processingStatus === 'completed') ? (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span>{t('upload.submit_button.complete')}</span>
                    </>
                  ) : processingStatus === 'processing' ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>{t('upload.submit_button.processing')}</span>
                    </>
                  ) : processingStatus === 'failed' ? (
                    <>
                      <AlertCircle className="w-5 h-5" />
                      <span>{t('upload.submit_button.failed')}</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span>{t('upload.submit_button.default')}</span>
                    </>
                  )}
                </button>

                {/* Info Text */}
                {selectedFile && !success && !processingStatus && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-blue-800 text-sm">
                      {t('upload.info.impact_desc')}
                      {t('upload.info.time_estimate')}
                    </p>
                    <p className="text-blue-700 text-xs mt-2">
                      <strong>Note:</strong> {t('upload.info.note_ocr')}
                    </p>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
